import AIOperations from './components/AIOperations';
import {defaultGovernance,consumeAction,type Governance} from './domain/governance';
import EnterpriseAccess from './components/EnterpriseAccess';
import RefineryWorkspace from './components/RefineryWorkspace';
import {SGS_LAB} from './domain/refinery';
import './components/refinery.css';
import SamplePlanner from './components/SamplePlanner';
import LabBot from './components/LabBot';
import { hydrateSaved } from './domain/migrate';
import {
  AttentionQueue,
  ShiftPlanner,
  ConnectorDirectory,
  EnterpriseConnections,
  PartsCatalog,
  Ordering,
  AuditBinder,
  KnowledgeHub,
} from "./components/Operations";
import Walkthrough from "./components/Walkthrough";
import { initialOps, failureModes } from "./domain/operations";
import { evidenceChecklist } from "./domain/records";
import { useEffect, useState, useRef, type ReactNode } from "react";
import {
  Activity,
  ArrowDownToLine,
  ArrowRight,
  Bell,
  BookOpen,
  Box,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleHelp,
  ClipboardCheck,
  Clock3,
  Cpu,
  Database,
  FileCheck2,
  FileText,
  FlaskConical,
  GitBranch,
  LayoutDashboard,
  LockKeyhole,
  Menu,
  Microscope,
  Network,
  Package,
  Play,
  RotateCcw,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  Users,
  X,
  AlertTriangle,
} from "lucide-react";
import {
  act,
  binder,
  evidence,
  exportPayload,
  log,
  seed,
  type Action,
  type DemoState,
  type Sample,
} from "./domain/demo";
import { CAPA_STATUS_LABELS } from "./domain/capa";
import { calculateRPN, getRPNRiskLevel } from "./domain/rpn";
import {
  instruments,
  labs,
  models,
  packs,
  roster,
  telemetry,
} from "./data/catalog";

type Page =
  | "overview"
  | "samples"
  | "detection"
  | "investigation"
  | "capa"
  | "fmea"
  | "schedule"
  | "skills"
  | "binder"
  | "connectors"
  | "models"
  | "wiki"
  | "marketplace"
  | "export"
  | "orders"
  | "enterprise"
  | "bot"
  | "aiops"
  | "planning";
const navigation: {
  section: string;
  items: [Page, string, typeof Activity][];
}[] = [
  {
    section: "LAB OPERATIONS",
    items: [
      ["overview", "Command center", LayoutDashboard],
      ["bot", "Lab Bot", Sparkles],
      ["aiops", "AI operations", Settings2],
      ["samples", "Customer work", FlaskConical],
      ["planning", "Sample planning", CalendarDays],
      ["detection", "Instrument & calibration", Activity],
      ["investigation", "Investigation", Sparkles],
    ],
  },
  {
    section: "QUALITY & EXECUTION",
    items: [
      ["capa", "Formal CAPA", ShieldCheck],
      ["fmea", "Risk & FMEA", GitBranch],
      ["schedule", "Work schedule", CalendarDays],
      ["skills", "Skills & training", Users],
      ["binder", "Audit binder", FileCheck2],
    ],
  },
  {
    section: "LAB INTELLIGENCE",
    items: [
      ["connectors", "Connector directory", Network],
      ["models", "Model library", Cpu],
      ["wiki", "Knowledge Hub", BookOpen],
      ["marketplace", "Parts & materials", Package],
      ["orders", "Ordering & approvals", ClipboardCheck],
      ["enterprise", "Enterprise connections", Database],
      ["export", "LIMS export", ArrowDownToLine],
    ],
  },
];
const titles = Object.fromEntries(
  navigation.flatMap((g) => g.items.map(([id, label]) => [id, label])),
) as Record<Page, string>;
const read = (lab: string): DemoState => {
  try {
    const s = JSON.parse(localStorage.getItem("labos:v1:" + lab) || (lab === "Houston · Central Laboratory" ? localStorage.getItem("labos:v1:" + ["Gulf", "Coast · Houston"].join(" ")) : null) || "null");
    const state = hydrateSaved(s,lab);
    state.lab=lab;
    return state;
  } catch {
    return seed(lab);
  }
};
const badge = (text: string, tone = "") => (
  <span
    className={`badge ${tone || (/Released|Connected|Complete|Passed|Closed|Retested|Installed/.test(text) ? "success" : /Held|Attention|High|Blocked/.test(text) ? "danger" : /Review|watch|Research|Pending/.test(text) ? "amber" : "")}`}
  >
    {text}
  </span>
);
function Button({
  children,
  onClick,
  primary = false,
  disabled = false,
  ...props
}: {
  children: ReactNode;
  onClick?: () => void;
  primary?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      className={primary ? "button primary" : "button"}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
}
function Panel({
  title,
  children,
  action,
  className = "",
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <section className={`panel ${className}`}>
      <div className="panel-head">
        <h2>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
function Download(name: string, content: string, type = "application/json") {
  const u = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement("a");
  a.href = u;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(u), 1000);
}
function Chart() {
  const plotX=(time:string)=>{const [h,m]=time.split(":").map(Number);return 40+((h*60+m-420)/360)*691;};
  return (
    <div className="chart">
      <svg
        viewBox="0 0 760 210"
        role="img"
        aria-label="Recorded demo instrument scores end at the 11:12 warning. Last QC at 07:00 was accepted; next QC is scheduled at 13:00."
      >
        <defs>
          <linearGradient id="fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0284c7" stopOpacity=".16" />
            <stop offset="100%" stopColor="#0284c7" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 0.35, 0.7, 1].map((n) => (
          <g key={n}>
            <line
              x1="40"
              x2="735"
              y1={175 - n * 140}
              y2={175 - n * 140}
              stroke="#e3e6eb"
              strokeDasharray={n === 0.7 ? "5 5" : undefined}
            />
            <text x="7" y={180 - n * 140}>
              {n.toFixed(2)}
            </text>
          </g>
        ))}
        <path
          d={
            "M40 175 " +
            telemetry
              .map(
                (t, i) =>
                  `L${plotX(t.time)} ${175 - t.score * 140}`,
              )
              .join(" ") +
            ` L${plotX(telemetry.at(-1)!.time)} 175 Z`
          }
          fill="url(#fill)"
        />
        <polyline
          points={telemetry
            .map(
              (t, i) =>
                `${plotX(t.time)},${175 - t.score * 140}`,
            )
            .join(" ")}
          fill="none"
          stroke="#0284c7"
          strokeWidth="3"
        />
        <line
          x1="40"
          x2="731"
          y1="161"
          y2="161"
          stroke="#afb6c1"
          strokeWidth="1.5"
          strokeDasharray="4 5"
        />
        <line
          x1="524"
          x2="524"
          y1="22"
          y2="178"
          stroke="#d88937"
          strokeDasharray="4 4"
        />
        <rect x="447" y="3" width="155" height="25" rx="5" fill="#fff4e4" />
        <text x="458" y="20" fill="#9b601c">
          11:12 · early warning
        </text>
        <text x="577" y="64" fill="#717a87">
          Alert threshold · 0.70
        </text>
        {["07:00", "08:30", "10:00", "11:30", "13:00"].map((v, i) => (
          <text key={v} x={40 + i * 170} y="203">
            {v}
          </text>
        ))}
      </svg>
      <div className="legend">
        <span>
          <i className="dot brand" />
          Instrument anomaly score
        </span>
        <span>
          <i className="dot gray" />
          Last QC accepted · 07:00
        </span>
        <span className="muted">Synthetic replay · CDT</span>
      </div>
    </div>
  );
}

export default function App() {
  const initialLab = new URLSearchParams(location.search).get("workspace") === "sgs" ? SGS_LAB : labs[0];
  const [governance,setGovernance]=useState<Governance>(()=>{try{return {...defaultGovernance,...JSON.parse(localStorage.getItem('labos:org:ai-policy')||'{}')};}catch{return defaultGovernance;}});
  function saveGovernance(g:Governance){localStorage.setItem('labos:org:ai-policy',JSON.stringify(g));setGovernance(g);}
  const [lab, setLab] = useState(initialLab);
  const [s, setS] = useState<DemoState>(() => read(initialLab));
  const [page, setPage] = useState<Page>(() => {
    const p = location.hash.slice(1);
    return p in titles ? (p as Page) : "overview";
  });
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [toast, setToast] = useState("");
  const [modal, setModal] = useState<{ title: string; body: ReactNode } | null>(
    null,
  );
  const [mobile, setMobile] = useState(false);
  const [guide, setGuide] = useState(false);
  const replayBackup = useRef<DemoState | null>(null);
  const [language] = useState("English · en-US");
  const [format, setFormat] = useState("CSV");
  const [wikiId, setWikiId] = useState("CTX-01");
  const [wikiText, setWikiText] = useState("");
  const [wikiTitle, setWikiTitle] = useState("");
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!guide) localStorage.setItem("labos:v1:" + s.lab, JSON.stringify(s));
  }, [s, guide]);
  useEffect(() => {
    const fn = () => {
      const p = location.hash.slice(1);
      if (p in titles) {
        setPage(p as Page);
        setFilter("All");
        setQuery("");
      }
    };
    window.addEventListener("hashchange", fn);
    return () => window.removeEventListener("hashchange", fn);
  }, []);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 4500);
    return () => clearTimeout(t);
  }, [toast]);
  useEffect(() => {
    if (!modal) return;
    const f = (e: KeyboardEvent) => {
      if (e.key === "Escape") setModal(null);
    };
    window.addEventListener("keydown", f);
    return () => window.removeEventListener("keydown", f);
  }, [modal]);
  function go(p: Page) {
    setPage(p);
    location.hash = p;
    setFilter("All");
    setQuery("");
    setMobile(false);
  }
  function mutate(fn: (d: DemoState) => void, message: string) {
    const d = structuredClone(s);
    fn(d);
    setS(d);
    setToast(message);
  }
  function run(action: Action) {
    try {
      setS(act(s, action));
      setToast("Saved to the lab history.");
    } catch (e) {
      setToast((e as Error).message);
    }
  }
  function inspect(title: string, body: ReactNode) {
    setModal({ title, body });
  }
  const match = (...v: string[]) =>
    v.join(" ").toLowerCase().includes(query.toLowerCase());
  const affected = s.samples.filter((x) => x.instrument === "DIST-04");
  const ev = evidenceChecklist(s);
  const ready = ev.filter((x) => x.complete).length;
  const rpn = calculateRPN(...s.ratings);
  function sampleDetail(x: Sample) {
    inspect(
      x.id,
      <>
        <div className="detail-grid">
          <div>
            <small>Customer / order</small>
            <strong>
              {x.customer} · {x.order}
            </strong>
          </div>
          <div>
            <small>Current disposition</small>
            {badge(x.status)}
          </div>
          <div>
            <small>Original instrument</small>
            <strong>{x.instrument}</strong>
          </div>
          <div>
            <small>Original T90</small>
            <strong>{x.t90} °C</strong>
          </div>
        </div>
        <h3>Traceable result history</h3>
        <ul className="timeline">
          <li>08:15 · Received sealed; chain of custody COC-1941</li>
          <li>
            {x.time} · Run completed on {x.instrument}; original result retained
          </li>
          {x.instrument === "DIST-04" && (
            <li>11:12 · Linked to ALERT-024 by instrument and onset window</li>
          )}
          {s.held && x.instrument === "DIST-04" && (
            <li>Manager approved result hold</li>
          )}
          {x.retest !== undefined && (
            <li>
              Retested on DIST-02 · {x.retest} °C · QC-612 passed; supersedes
              original
            </li>
          )}
          {x.status === "Released" && (
            <li>Maya Chen authorized customer release · COA-1042</li>
          )}
        </ul>
        <p className="note">
          Synthetic sample record. Values are illustrative; product acceptance
          limits require the customer contract and method-specific review.
        </p>
        <Button
          primary
          onClick={() => {
            setModal(null);
            go(x.status === "Released" ? "export" : "investigation");
          }}
        >
          Open linked workflow <ArrowRight size={14} />
        </Button>
      </>,
    );
  }
  function sampleTable(rows: Sample[]) {
    return (
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Sample / order</th>
              <th>Customer</th>
              <th>Material</th>
              <th>Instrument</th>
              <th>T90 · °C</th>
              <th>Disposition</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((x) => (
              <tr key={x.id}>
                <td>
                  <button className="text-link" onClick={() => sampleDetail(x)}>
                    {x.id}
                  </button>
                  <small>{x.order}</small>
                </td>
                <td>{x.customer}</td>
                <td>{x.product}</td>
                <td className="mono">
                  {x.retest !== undefined ? "DIST-02 ↺" : x.instrument}
                </td>
                <td className="mono">
                  {x.retest ?? x.t90}
                  {x.retest !== undefined && (
                    <small className="strike">{x.t90}</small>
                  )}
                </td>
                <td>{badge(x.status)}</td>
                <td>
                  <button
                    className="icon-button"
                    aria-label={`Open ${x.id}`}
                    onClick={() => sampleDetail(x)}
                  >
                    <ChevronRight size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div className="empty">No samples match your filters.</div>
        )}
      </div>
    );
  }
  const nextAction: Record<string, [string, Action]> = {
    open: ["Start investigation", "investigate"],
    investigating: ["Submit evidenced root cause", "conclude"],
    root_cause_review: ["Approve root cause", "sign"],
    planning: ["Submit action plan", "plan"],
    plan_review: ["Approve plan & schedule work", "approve"],
    in_progress: ["Record completed corrective work", "correct"],
    reopened: ["Restart investigation", "investigate"],
  };
  const next = nextAction[s.capa.status];
  const heroStep =
    !s.held && !s.delivered
      ? ["Contain affected samples", "hold"]
      : s.capa.status === "open"
        ? ["Investigate the cause", "investigate"]
        : !s.verified
          ? ["Continue CAPA workflow", "capa"]
          : !s.delivered
            ? ["Review customer release", "samples"]
            : ["Assemble evidence package", "binder"];

  return (
    <div className={`app-shell ${guide ? "tour-active" : ""}`}>
      <aside className={mobile ? "sidebar open" : "sidebar"}>
        <a href="#overview" className="brand" onClick={() => go("overview")}>
          <span className="brand-mark">
            <Activity size={22} />
          </span>
          <span>
            trucision<small>LAB OPERATING SYSTEM</small>
          </span>
        </a>
        <div className="lab-label">
          <span className="live-dot" /> LAB WORKSPACE
        </div>
        <select
          aria-label="Select lab"
          className="lab-select"
          value={lab}
          onChange={(e) => {
            setLab(e.target.value);
            const url = new URL(location.href);
            if(e.target.value === SGS_LAB) url.searchParams.set("workspace","sgs"); else url.searchParams.delete("workspace");
            history.replaceState(null,"",url);
            setS(read(e.target.value));
            setEditing(false);
            setToast(
              "Switched lab. Each lab has independent context and demo history.",
            );
          }}
        >
          {labs.map((l) => (
            <option key={l}>{l}</option>
          ))}
        </select>
        <nav>
          {navigation.map((g) => (
            <div key={g.section} className="nav-group">
              <div className="nav-label">{g.section}</div>
              {g.items.map(([id, label, Icon]) => (
                <button
                  key={id}
                  className={`nav-item ${id === "aiops" ? "nav-child" : ""} ${page === id ? "active" : ""}`}
                  onClick={() => go(id)}
                >
                  <Icon size={16} />
                  <span>{label}</span>
                  {id === "detection" && !s.delivered && <b>1</b>}
                  {id === "capa" && s.capa.status !== "closed" && (
                    <span className="nav-count">1</span>
                  )}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <span className="avatar">MC</span>
          <div>
            <strong>Maya Chen</strong>
            <small>Lab manager</small>
          </div>
          <ShieldCheck size={16} />
        </div>
      </aside>
      <div className="workspace">
        <header className="topbar">
          <button
            className="icon-button mobile-menu"
            onClick={() => setMobile(!mobile)}
            aria-label="Toggle navigation"
          >
            <Menu size={20} />
          </button>
          <div className="breadcrumbs">
            Workspace <ChevronRight size={13} />
            <strong>{titles[page]}</strong>
          </div>
          <div className="top-actions">
            {lab === SGS_LAB && <div className="sgs-brand" aria-label="SGS illustrative demo"><strong>SGS</strong><small>REFINERY SERVICES · DEMO</small></div>}
            {lab !== SGS_LAB && <div className="digital-twin-brand" aria-label="Digital Twin · simulated laboratory"><span className="twin-symbol" aria-hidden="true">◇</span><div><strong>Digital Twin</strong><small>SIMULATED LABORATORY</small></div></div>}
            <span className="demo-badge">
              <span className="live-dot" /> DEMO DATA
            </span>
            <div className="workspace-context">
              <span>
                <b>ORG</b> {lab === SGS_LAB ? "SGS · illustrative demo" : "Trucision Laboratory Services"}
              </span>
              <span>
                <b>LAB</b> {lab}
              </span>
              <span>
                <b>LANG</b> {language}
              </span>
              <span>
                <b>TODAY</b> {s.watchAdvanced ? "13 Nov" : "13 Oct"} 2026 · demo
                day
              </span>
            </div>
            <button
              className="icon-button"
              aria-label="Notifications"
              onClick={() =>
                inspect(
                  "Shift notifications",
                  <ul className="timeline">
                    {s.audit
                      .slice(-5)
                      .reverse()
                      .map((e) => (
                        <li key={e.id}>
                          <strong>{e.action}</strong>
                          <p>{e.detail}</p>
                        </li>
                      ))}
                  </ul>,
                )
              }
            >
              <Bell size={17} />
            </button>
            <button
              className="icon-button"
              aria-label="Demo information"
              onClick={() =>
                inspect(
                  "About this demonstration",
                  <>
                    <p>
                      A connected, synthetic laboratory scenario. Every
                      customer, measurement, prediction and approval shown here
                      is mock data. Actions persist in this browser, separately
                      for each lab.
                    </p>
                    <p>
                      Model cards describe supported tasks and readiness.
                      Predictions and connector responses are simulated. ISO
                      clause mappings support an illustrative evidence package;
                      they do not establish accreditation or compliance.
                    </p>
                    <p>
                      Formal CAPA transitions and RPN calculations are imported
                      from the existing Trucision prototype. No customer emails
                      or live LIMS transmissions are sent.
                    </p>
                  </>,
                )
              }
            >
              <CircleHelp size={17} />
            </button>
          </div>
        </header>
        <main data-page={page}>
          {lab === SGS_LAB && <div className="refinery-disclosure">Illustrative SGS Houston workspace · Bayou Bend Refining is fictional · Synthetic records; no SGS endorsement or accreditation claim.{!["overview","planning"].includes(page) && " Shared capability demo: Northstar examples are separate from the refinery intake queue."}</div>}
          <div className="page-heading">
            <div>
              <div className="eyebrow">
                {page === "overview"
                  ? "YOUR LAB, UNDER CONTROL"
                  : "TRUCISION / " + lab.split(" · ")[0].toUpperCase()}
              </div>
              <h1>{titles[page]}</h1>
              <p>
                {
                  {
                    overview:
                      "Keep customer work moving. Catch quality risk before it becomes a reported result.",
                    samples:
                      "From received sample to a defensible customer delivery.",
                    detection:
                      "Detect instrument faults, calibration risk and their impact on customer results.",
                    investigation:
                      "Follow the evidence. Keep the decision with the lab.",
                    capa: "One controlled record, from containment through effectiveness.",
                    fmea: "Prioritize failure modes with consistent severity, occurrence and detection.",
                    aiops: "Runtime governance, agent permissions and operating limits for IT and lab leadership.",
                    planning: "Track each sample, method and attempt from arrival to authorized release.",
                    schedule:
                      "The right work, on the right instrument, with a qualified analyst.",
                    skills:
                      "Competency tied to methods, authorizations and work evidence.",
                    binder:
                      "A traceable package assembled from what actually happened in this demo.",
                    connectors:
                      "A directory of instrument integrations, signals and readiness.",
                    models:
                      "Prediction and AI services for laboratory operations.",
                    wiki: "Versioned instrument knowledge, SOPs, governance, workflow and exception records.",
                    marketplace:
                      "Request instrument parts and laboratory materials with controlled purchasing.",
                    orders:
                      "Director approval, procurement clarification and tracked receipt.",
                    bot: "Your assistant for customer communication, supplier follow-up and routine lab administration.",
                    enterprise:
                      "Permission-based, read-only access to ERP and collaboration sources.",
                    export:
                      "Release-controlled results, explicit mappings and an export ledger.",
                  }[page]
                }
              </p>
            </div>
            <div className="heading-actions">
              <Button
                onClick={() => {
                  replayBackup.current = structuredClone(s);
                  setGuide(true);
                }}
              >
                <Play size={13} /> Animated walkthrough
              </Button>
              <button
                className="icon-button"
                aria-label="Reset demo"
                onClick={() =>
                  inspect(
                    "Reset this lab’s demo?",
                    <>
                      <p>
                        This resets only {lab} in this browser. Download the
                        evidence package first if you want to keep this run.
                      </p>
                      <Button
                        primary
                        onClick={() => {
                          setS(seed(lab));
                          setModal(null);
                          go("overview");
                          setToast("Fresh demo loaded.");
                        }}
                      >
                        Reset demo
                      </Button>
                    </>,
                  )
                }
              >
                <RotateCcw size={15} />
              </button>
            </div>
          </div>
          {guide && (
            <Walkthrough
              lab={lab}
              onScene={(next, p) => {
                setS(next);
                setPage(p as Page);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              onClose={() => {
                if (replayBackup.current) setS(replayBackup.current);
                setGuide(false);
                go("overview");
              }}
            />
          )}

          {page === "overview" && lab === SGS_LAB && <RefineryWorkspace s={s} go={go} />}
          {page === "overview" && lab !== SGS_LAB && (
            <>
              <div className="metrics">
                <Metric
                  label="Customer samples"
                  value="12"
                  detail="3 orders · all due today"
                  icon={<FlaskConical />}
                />
                <Metric
                  label={
                    s.held || s.delivered ? "Quality hold" : "At-risk samples"
                  }
                  value={s.delivered ? "0" : "6"}
                  detail={
                    s.delivered
                      ? "All samples authorized"
                      : s.held
                        ? "Containment approved"
                        : "Awaiting containment approval"
                  }
                  tone={s.delivered ? "brand" : "orange"}
                  icon={<ShieldCheck />}
                />
                <Metric
                  label="Earlier visibility"
                  value="108 min"
                  detail="Before next scheduled routine QC"
                  tone="brand"
                  icon={<Activity />}
                />
                <Metric
                  label="Evidence coverage"
                  value={`${ready} / ${ev.length}`}
                  detail="Linked records in this scenario"
                  icon={<FileCheck2 />}
                />
              </div>
              <div className="split-main">
                <div>
                  <section
                    className={`incident ${s.delivered ? "resolved" : ""}`}
                  >
                    <div className="incident-top">
                      <span className="eyebrow">
                        {s.delivered
                          ? "CUSTOMER DELIVERY PROTECTED"
                          : "REQUIRES YOUR DECISION"}
                      </span>
                      {badge(
                        s.delivered ? "Released" : "Early warning",
                        s.delivered ? "success" : "amber",
                      )}
                    </div>
                    <h2>
                      {s.delivered
                        ? "Northstar’s results are ready."
                        : "DIST-04: heating response requires investigation."}
                    </h2>
                    <p>
                      DIST-04 is showing an abnormal heating response. Six
                      Northstar diesel samples fall inside the affected window.
                    </p>
                    <div className="incident-meta">
                      <span>
                        <Clock3 size={14} /> Detected 11:12
                      </span>
                      <span>
                        <FlaskConical size={14} /> 6 affected samples
                      </span>
                      <span>
                        <FileText size={14} /> NS-1042 · due 16:00
                      </span>
                    </div>
                    <div className="row">
                      <Button
                        primary
                        onClick={() => {
                          if (
                            heroStep[1] === "hold" ||
                            heroStep[1] === "investigate"
                          )
                            run(heroStep[1] as Action);
                          else go(heroStep[1] as Page);
                        }}
                      >
                        {heroStep[0]} <ArrowRight size={14} />
                      </Button>
                      <Button onClick={() => go("detection")}>
                        Review signal
                      </Button>
                    </div>
                  </section>
                  <AttentionQueue s={s} mutate={mutate} go={go} />
                  <Panel
                    title="Instrument behavior · DIST-04"
                    action={
                      <button
                        className="text-link"
                        onClick={() => go("detection")}
                      >
                        Explore <ArrowRight size={12} />
                      </button>
                    }
                  >
                    <Chart />
                  </Panel>
                </div>
                <Panel
                  title="Today’s customer commitments"
                  className="commitments"
                >
                  {[
                    ["Northstar Fuels", "NS-1042", "6 samples", "16:00"],
                    ["Meridian Trading", "MT-8821", "3 samples", "15:30"],
                    ["Harbor Energy", "HE-2209", "3 samples", "17:00"],
                  ].map(([name, id, count, due], i) => (
                    <button
                      className="commitment"
                      key={id}
                      onClick={() => {
                        go("samples");
                        setQuery(name);
                      }}
                    >
                      <span className={`customer-logo logo-${i}`}>
                        {name
                          .split(" ")
                          .map((w) => w[0])
                          .join("")}
                      </span>
                      <div>
                        <strong>{name}</strong>
                        <small>
                          {id} · {count}
                        </small>
                        <div className="commitment-line">
                          <span>Due {due}</span>
                          {badge(
                            s.delivered
                              ? "Released"
                              : i === 0
                                ? "At risk"
                                : "In review",
                            s.delivered ? "success" : i === 0 ? "amber" : "",
                          )}
                        </div>
                      </div>
                      <ChevronRight size={16} />
                    </button>
                  ))}
                  <div className="promise">
                    <ShieldCheck size={20} />
                    <strong>Human-authorized release</strong>
                    <p>
                      Agents prepare the evidence. You decide what leaves the
                      lab.
                    </p>
                    <button className="text-link" onClick={() => go("samples")}>
                      Open delivery queue <ArrowRight size={13} />
                    </button>
                  </div>
                </Panel>
              </div>
              <Panel
                title="Recent lab activity"
                action={
                  <button className="text-link" onClick={() => go("binder")}>
                    Full evidence trail
                  </button>
                }
              >
                <div className="activity-strip">
                  {s.audit
                    .slice(-3)
                    .reverse()
                    .map((e) => (
                      <div key={e.id}>
                        <span className="activity-dot" />
                        <small>
                          {e.at.slice(11, 16)} UTC · {e.actor}
                        </small>
                        <strong>{e.action}</strong>
                        <p>{e.detail}</p>
                      </div>
                    ))}
                </div>
              </Panel>
            </>
          )}

          {page === "samples" && (
            <>
              <div className="compact-banner">
                <div>
                  <strong>NS-1042 · Northstar Fuels</strong>
                  <span>
                    Six affected results stay blocked until retest and manager
                    approval.
                  </span>
                </div>
                {s.delivered ? (
                  badge("Customer release approved", "success")
                ) : (
                  <Button
                    primary
                    disabled={!s.verified}
                    onClick={() => run("release")}
                  >
                    Approve customer release <ArrowRight size={14} />
                  </Button>
                )}
              </div>
              <div className="toolbar">
                <div className="pills">
                  {["All", "Held", "Review", "Retested", "Released"].map(
                    (v) => (
                      <button
                        className={filter === v ? "selected" : ""}
                        onClick={() => setFilter(v)}
                        key={v}
                      >
                        {v}
                      </button>
                    ),
                  )}
                </div>
                <SearchBox
                  value={query}
                  onChange={setQuery}
                  placeholder="Find sample, customer or order"
                />
              </div>
              <Panel
                title="Customer sample queue"
                action={<span className="muted">12 samples · 3 orders</span>}
              >
                {sampleTable(
                  s.samples.filter(
                    (x) =>
                      (filter === "All" || x.status === filter) &&
                      match(x.id, x.customer, x.order),
                  ),
                )}
              </Panel>
              {!s.verified && (
                <div className="note">
                  Release gate: complete the CAPA corrective work and approve
                  recovery on the CAPA page. Original results are retained; only
                  retest values will be delivered for the affected samples.{" "}
                  <button className="text-link" onClick={() => go("capa")}>
                    Open CAPA →
                  </button>
                </div>
              )}
              {s.delivered && (
                <div className="row section-gap">
                  <Button primary onClick={() => go("export")}>
                    Prepare LIMS export <ArrowRight size={14} />
                  </Button>
                  <Button onClick={() => go("binder")}>
                    Open delivery evidence
                  </Button>
                </div>
              )}
            </>
          )}

          {page === "detection" && (
            <>
              <div className="metrics">
                <Metric
                  label="Instrument anomaly"
                  value="0.82"
                  detail="Threshold 0.70 · 3 windows"
                  tone="orange"
                />
                <Metric
                  label="Latest routine QC"
                  value="In control"
                  detail="07:00 · z-score 0.4"
                  tone="brand"
                />
                <Metric
                  label="Next routine QC"
                  value="13:00"
                  detail="108 minutes after warning"
                />
                <Metric
                  label="Potential exposure"
                  value="6 samples"
                  detail="Since onset at 07:32"
                />
              </div>
              <div className="split-main">
                <Panel title="Early signal vs. routine QC">
                  <Chart />
                  <div className="panel-body note">
                    Replay is synthetic. The 108 minutes measures
                    warning-to-next-scheduled-QC; it is not a measured
                    performance claim that QC would have failed then.
                  </div>
                </Panel>
                <Panel title="Why the alert fired">
                  <div className="panel-body">
                    <div className="signal-row">
                      <span>Heater response residual</span>
                      <strong>+3.2σ</strong>
                    </div>
                    <div className="signal-row">
                      <span>Recovery profile shift</span>
                      <strong>+1.8%</strong>
                    </div>
                    <div className="signal-row">
                      <span>Consecutive abnormal windows</span>
                      <strong>3 / 3</strong>
                    </div>
                    <div className="signal-row">
                      <span>Context policy</span>
                      <button className="text-link" onClick={() => go("wiki")}>
                        CTX-01 · rev 4
                      </button>
                    </div>
                    <p className="muted">
                      A warning of possible result bias. Investigation must
                      establish the cause and impact.
                    </p>
                    <Button
                      primary
                      disabled={s.held || s.delivered}
                      onClick={() => run("hold")}
                    >
                      {s.held
                        ? "Samples contained"
                        : s.delivered
                          ? "Case released"
                          : "Approve containment"}
                    </Button>
                  </div>
                </Panel>
              </div>
              <Panel
                title="Affected sample scope"
                action={
                  <button
                    className="text-link"
                    onClick={() => go("investigation")}
                  >
                    Investigate linked evidence →
                  </button>
                }
              >
                {sampleTable(affected)}
              </Panel>
            </>
          )}

          {page === "investigation" && (
            <>
              <div className="agent-banner">
                <span className="agent-icon">
                  <Sparkles size={24} />
                </span>
                <div>
                  <span className="eyebrow">
                    QUALITY INVESTIGATOR · SIMULATED AGENT
                  </span>
                  <h2>
                    Likely heating-contact issue. Six results need review.
                  </h2>
                  <p>
                    The alert overlaps the flask setup change. Probe and
                    preparation evidence help separate competing explanations.
                  </p>
                </div>
                {badge(
                  s.investigated
                    ? "Evidence recorded"
                    : "Awaiting investigation",
                  s.investigated ? "success" : "amber",
                )}
              </div>
              <div className="three-grid">
                {[
                  [
                    "01",
                    "Scope agent",
                    "6 linked samples",
                    "Matched DIST-04 runs after 07:32 to order NS-1042.",
                    "TRACE-884 · COC-1941",
                  ],
                  [
                    "02",
                    "Investigation agent",
                    "3 competing hypotheses",
                    "Compare heater contact, probe calibration and sample preparation.",
                    "SOP-D86-014 · CAL-184",
                  ],
                  [
                    "03",
                    "Decision agent",
                    "Contain, inspect, retest",
                    "Qualified analyst investigates; manager reviews the conclusion.",
                    "CTX-01 · COMP-041",
                  ],
                ].map(([n, title, big, desc, ref]) => (
                  <Panel key={n} title={title}>
                    <div className="panel-body">
                      <span className="step-number">{n}</span>
                      <h3>{big}</h3>
                      <p>{desc}</p>
                      <span className="source-chip">{ref}</span>
                    </div>
                  </Panel>
                ))}
              </div>
              <div className="split-main">
                <Panel title="Hypotheses & supporting evidence">
                  <div className="hypotheses">
                    {s.capa.candidateCauses.map((c, i) => (
                      <div key={c.id}>
                        <div className="row between">
                          <strong>{c.description}</strong>
                          {badge(
                            i === 0 ? "Leading hypothesis" : "Alternative",
                          )}
                        </div>
                        <div className="confidence">
                          <span style={{ width: c.likelihood + "%" }} />
                        </div>
                        <p>
                          {i === 0
                            ? "Heater demand increased after setup; simulated inspection finds a 1.8 mm seating gap."
                            : i === 1
                              ? "Reference thermometer differs by +0.1 °C; does not explain the observed shift."
                              : "Same operator, sample preparation and CRM lot; no logged preparation change."}
                        </p>
                        <small>
                          Agent ranking {c.likelihood}% · illustrative, not
                          calibrated probability
                        </small>
                      </div>
                    ))}
                  </div>
                </Panel>
                <Panel title="Defensible next action">
                  <div className="panel-body">
                    <ul className="check-list">
                      <li>
                        <CheckCircle2 />
                        Contain affected result window
                      </li>
                      <li>
                        <CheckCircle2 />
                        Inspect and record physical evidence
                      </li>
                      <li>
                        <CheckCircle2 />
                        Independently approve root cause
                      </li>
                      <li>
                        <CheckCircle2 />
                        Correct, verify QC and retest aliquots
                      </li>
                    </ul>
                    <p className="note">
                      Do not discard original results or infer customer
                      conformity from the anomaly score.
                    </p>
                    {!s.held && !s.delivered ? (
                      <Button primary onClick={() => run("hold")}>
                        Approve containment
                      </Button>
                    ) : s.capa.status === "open" ? (
                      <Button primary onClick={() => run("investigate")}>
                        Run simulated investigation
                      </Button>
                    ) : (
                      <Button primary onClick={() => go("capa")}>
                        Review CAPA-026 <ArrowRight size={14} />
                      </Button>
                    )}
                  </div>
                </Panel>
              </div>
            </>
          )}

          {page === "capa" && (
            <>
              <div className="compact-banner">
                <div>
                  <strong>CAPA-026 · Recurring heating response drift</strong>
                  <span>DIST-04 · Owner Maya Chen · Due 12 Nov 2026</span>
                </div>
                {badge(
                  CAPA_STATUS_LABELS[s.capa.status],
                  s.capa.status === "closed" ? "success" : "amber",
                )}
              </div>
              <div className="steps">
                {[
                  "open",
                  "investigating",
                  "root_cause_review",
                  "planning",
                  "plan_review",
                  "in_progress",
                  "effectiveness_watch",
                  "closed",
                ].map((p, i) => (
                  <div key={p} className={s.capa.status === p ? "current" : ""}>
                    <span>{i + 1}</span>
                    {CAPA_STATUS_LABELS[p as keyof typeof CAPA_STATUS_LABELS]}
                  </div>
                ))}
              </div>
              <div className="split-main">
                <Panel
                  title="Controlled workflow"
                  action={
                    <span className="source-chip">
                      Imported Trucision state machine
                    </span>
                  }
                >
                  <div className="panel-body">
                    <h3>{CAPA_STATUS_LABELS[s.capa.status]}</h3>
                    <p>
                      {s.capa.rootCause?.statement ||
                        "Investigate recurring heating response drift. The root cause must be evidenced and independently reviewed before corrective work is approved."}
                    </p>
                    {s.capa.rootCause && (
                      <p className="muted">
                        Concluded by {s.capa.rootCause.concludedBy}
                        {s.capa.rootCause.signedOffBy &&
                          ` · Approved by ${s.capa.rootCause.signedOffBy}`}
                      </p>
                    )}
                    {s.capa.plan.actions.length > 0 && (
                      <div className="action-list">
                        {s.capa.plan.actions.map((a) => (
                          <div key={a.id}>
                            <CheckCircle2
                              size={17}
                              color={s.corrected ? "#0284c7" : "#9ca4af"}
                            />
                            <div>
                              <strong>{a.description}</strong>
                              <small>
                                {a.kind} · {a.assignee} · {a.workRecordId}
                              </small>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="row wrap">
                      {!s.held && !s.delivered && (
                        <Button primary onClick={() => run("hold")}>
                          Contain samples first
                        </Button>
                      )}
                      {next && (
                        <Button
                          primary
                          disabled={!s.held && !s.delivered}
                          onClick={() => run(next[1])}
                        >
                          {next[0]} <ArrowRight size={14} />
                        </Button>
                      )}
                      {s.capa.status === "effectiveness_watch" &&
                        !s.verified && (
                          <Button primary onClick={() => run("verify")}>
                            Approve QC recovery & six retests
                          </Button>
                        )}
                      {s.verified && !s.delivered && (
                        <Button primary onClick={() => go("samples")}>
                          Review customer release
                        </Button>
                      )}
                      {s.capa.status === "effectiveness_watch" &&
                        s.capa.effectiveness?.result !== "passed" && (
                          <Button
                            disabled={!s.verified}
                            onClick={() => run("watch")}
                          >
                            Simulate 30-day effectiveness window
                          </Button>
                        )}
                      {s.capa.effectiveness?.result === "passed" &&
                        s.capa.status !== "closed" && (
                          <Button primary onClick={() => run("close")}>
                            Approve CAPA closure
                          </Button>
                        )}
                    </div>
                    {s.capa.status === "effectiveness_watch" && (
                      <p className="note">
                        Customer results can be released after verified retests;
                        formal CAPA closure separately requires 30 days with
                        zero recurrence. Advancing the window is an explicit
                        demo simulation.
                      </p>
                    )}
                    {s.capa.status === "closed" && (
                      <div className="success">
                        <CheckCircle2 />
                        Closed by Maya Chen after the simulated effectiveness
                        review.
                      </div>
                    )}
                  </div>
                </Panel>
                <Panel title="Gates & accountability">
                  <div className="panel-body">
                    <div className="signal-row">
                      <span>Containment</span>
                      {badge(
                        s.held
                          ? "Active"
                          : s.delivered
                            ? "Released"
                            : "Pending",
                      )}
                    </div>
                    <div className="signal-row">
                      <span>Investigation record</span>
                      {badge(s.investigated ? "Complete" : "Pending")}
                    </div>
                    <div className="signal-row">
                      <span>Corrective work</span>
                      {badge(s.corrected ? "Complete" : "Pending")}
                    </div>
                    <div className="signal-row">
                      <span>Recovery QC</span>
                      {badge(s.verified ? "Passed" : "Pending")}
                    </div>
                    <div className="signal-row">
                      <span>Effectiveness</span>
                      {badge(s.capa.effectiveness?.result || "Pending")}
                    </div>
                    <p className="muted">
                      Luis Ortega records the investigation and submits the
                      plan. Maya Chen independently approves the conclusion and
                      plan.
                    </p>
                    {s.capa.status === "effectiveness_watch" &&
                      s.capa.effectiveness?.result === "watching" && (
                        <button
                          className="text-link danger"
                          onClick={() => run("recurrence")}
                        >
                          Test alternate path: simulate recurrence
                        </button>
                      )}
                  </div>
                </Panel>
              </div>
              <Panel title="CAPA decision history">
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Event</th>
                        <th>Actor</th>
                        <th>From</th>
                        <th>To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.capa.history.map((h, i) => (
                        <tr key={i}>
                          <td>{h.event.replaceAll("_", " ")}</td>
                          <td>{h.by}</td>
                          <td>{CAPA_STATUS_LABELS[h.from]}</td>
                          <td>{CAPA_STATUS_LABELS[h.to]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!s.capa.history.length && (
                    <p className="empty">
                      Start the investigation to create the first workflow
                      record.
                    </p>
                  )}
                </div>
              </Panel>
            </>
          )}

          {page === "fmea" && (
            <>
              <div className="split-main">
                <Panel title="FMEA · heating response drift">
                  <div className="panel-body">
                    <div className="detail-grid">
                      <div>
                        <small>Failure mode</small>
                        <strong>
                          FM-D86-001 · Flask / heater contact loss
                        </strong>
                      </div>
                      <div>
                        <small>Effect on results</small>
                        <strong>Potential distillation profile bias</strong>
                      </div>
                      <div>
                        <small>Current control</small>
                        <strong>Routine QC + drift sentinel</strong>
                      </div>
                      <div>
                        <small>Linked action</small>
                        <button
                          className="text-link"
                          onClick={() => go("capa")}
                        >
                          CAPA-026
                        </button>
                      </div>
                    </div>
                    {["Severity", "Occurrence", "Detection difficulty"].map(
                      (label, i) => (
                        <label className="range-field" key={label}>
                          <span>
                            {label}
                            <small>
                              {
                                [
                                  "Impact if the failure reaches a customer",
                                  "Frequency of the failure mode",
                                  "Higher = harder to detect before release",
                                ][i]
                              }
                            </small>
                          </span>
                          <input
                            aria-label={label}
                            type="range"
                            min="1"
                            max="10"
                            value={s.ratings[i]}
                            onChange={(e) => {
                              const ratings = [...s.ratings] as [
                                number,
                                number,
                                number,
                              ];
                              ratings[i] = Number(e.target.value);
                              setS({ ...s, ratings });
                            }}
                          />
                          <strong>{s.ratings[i]}</strong>
                        </label>
                      ),
                    )}
                    <Button
                      onClick={() =>
                        mutate(
                          (d) =>
                            log(
                              d,
                              "FMEA assessment saved",
                              "FMEA-018",
                              `S ${d.ratings[0]} × O ${d.ratings[1]} × D ${d.ratings[2]} = ${calculateRPN(...d.ratings)}`,
                            ),
                          "Risk assessment saved to evidence history.",
                        )
                      }
                    >
                      Save assessment
                    </Button>
                  </div>
                </Panel>
                <Panel title="Risk priority number">
                  <div className="rpn-block">
                    <span className="eyebrow">
                      SEVERITY × OCCURRENCE × DETECTION
                    </span>
                    <strong>{rpn}</strong>
                    {badge(
                      getRPNRiskLevel(rpn).label,
                      rpn > 500 ? "danger" : rpn > 100 ? "amber" : "success",
                    )}
                    <p>
                      {s.ratings.join(" × ")} = {rpn}
                    </p>
                    <small>
                      Existing Trucision thresholds: low ≤100; medium 101–500;
                      high &gt;500. RPN supports prioritization; it does not
                      override a result hold.
                    </small>
                  </div>
                </Panel>
              </div>
              <Panel title="Failure mode register · local controlled codes, revision 1">
                <table>
                  <thead>
                    <tr>
                      <th>Failure mode</th>
                      <th>Instrument</th>
                      <th>S / O / D</th>
                      <th>RPN</th>
                      <th>Control</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <strong>FM-D86-001</strong>
                        <small>Heating contact drift</small>
                      </td>
                      <td>DIST-04</td>
                      <td>{s.ratings.join(" / ")}</td>
                      <td>{rpn}</td>
                      <td>CAPA-026 · active review</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>FM-D93-002</strong>
                        <small>Ignition-source wear</small>
                      </td>
                      <td>FLASH-01</td>
                      <td>7 / 3 / 4</td>
                      <td>84</td>
                      <td>Weekly inspection</td>
                    </tr>
                    <tr>
                      <td>
                        <strong>FM-DEN-003</strong>
                        <small>Density-cell contamination</small>
                      </td>
                      <td>DENS-01</td>
                      <td>6 / 3 / 3</td>
                      <td>54</td>
                      <td>Blank check per batch</td>
                    </tr>
                  </tbody>
                </table>
              </Panel>
            </>
          )}

          {page === "schedule" && (
            <ShiftPlanner s={s} mutate={mutate} go={go} />
          )}

          {page === "skills" && (
            <>
              <Panel title="Method competency & authorization">
                <div className="table-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Person</th>
                        <th>Role</th>
                        <th>Level</th>
                        <th>Authorized skills</th>
                        <th>Valid through</th>
                        <th>Evidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roster.map((r) => (
                        <tr key={r.name}>
                          <td>
                            <div className="person">
                              <span className="avatar light">
                                {r.name
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")}
                              </span>
                              <strong>{r.name}</strong>
                            </div>
                          </td>
                          <td>{r.role}</td>
                          <td>{badge("Level " + r.level)}</td>
                          <td>{r.skills.join(" · ")}</td>
                          <td>{r.expires}</td>
                          <td>
                            <button
                              className="text-link"
                              onClick={() =>
                                inspect(
                                  r.evidence,
                                  <>
                                    <h3>{r.name} · competency record</h3>
                                    <p>
                                      Observed method execution, supervised QC
                                      run and documented sign-off. Synthetic
                                      competency assessment valid through{" "}
                                      {r.expires}.
                                    </p>
                                    <p>Authorized: {r.skills.join(", ")}.</p>
                                    <p>
                                      Level {r.level};{" "}
                                      {r.level < 2
                                        ? "supervision required; cannot be assigned corrective D86 work."
                                        : "eligible for method work within recorded scope."}
                                    </p>
                                  </>,
                                )
                              }
                            >
                              {r.evidence} ↗
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
              <div className="two-grid">
                <Panel title="Training linked to corrective action">
                  <div className="panel-body">
                    <h3>SOP-D86-014 · seating verification</h3>
                    <p>
                      CAPA-026 introduces a documented flask-seating check. The
                      preventive action records an analyst briefing and revision
                      history.
                    </p>
                    {badge(
                      s.corrected
                        ? "Briefing complete"
                        : "Awaiting approved CAPA action",
                      s.corrected ? "success" : "amber",
                    )}
                    <div className="section-gap">
                      <Button onClick={() => go("capa")}>
                        Open training action
                      </Button>
                    </div>
                  </div>
                </Panel>
                <Panel title="Assignment policy">
                  <div className="panel-body">
                    <LockKeyhole size={25} />
                    <h3>Qualified people, traceable decisions</h3>
                    <p>
                      Scheduling offers Level 2+ analysts. Root-cause approval
                      is independent of the investigator; CAPA plan approval is
                      independent of its submitter.
                    </p>
                    <p className="note">
                      Roles are demo personas, not authenticated access control.
                    </p>
                  </div>
                </Panel>
              </div>
            </>
          )}

          {page === "binder" && <AuditBinder s={s} />}

          {page === "connectors" && (
            <ConnectorDirectory s={s} mutate={mutate} go={go} />
          )}

          {page === "models" && (
            <>
              <div className="compact-banner">
                <div>
                  <strong>Prediction services. Clear deployment readiness.</strong>
                  <span>
                    Model catalog · Demo
                    predictions are deterministic simulations.
                  </span>
                </div>
                <span className="source-chip">10 model & AI offerings</span>
              </div>
              <div className="toolbar">
                <div className="pills">
                  {["All", "Demo active", "Research"].map((f) => (
                    <button
                      key={f}
                      className={filter === f ? "selected" : ""}
                      onClick={() => setFilter(f)}
                    >
                      {f}
                    </button>
                  ))}
                </div>
                <SearchBox
                  value={query}
                  onChange={setQuery}
                  placeholder="Find model or task"
                />
              </div>
              <div className="two-grid">
                {models
                  .filter(
                    (m) =>
                      match(m.name, m.repo, m.family) &&
                      (filter === "All" || filter === m.stage),
                  )
                  .map((m) => (
                    <Panel
                      key={m.id}
                      title={m.name}
                      action={badge(
                        m.stage,
                        m.stage === "Demo active" ? "success" : "",
                      )}
                    >
                      <div className="panel-body">
                        <div className="row between">
                          <span className="source-chip">{m.family}</span>
                          <span className="source-chip">{m.id}</span>
                        </div>
                        <p>{m.use}</p>
                        <div className="model-io">
                          <div>
                            <small>INPUT</small>
                            {m.input}
                          </div>
                          <ArrowRight size={16} />
                          <div>
                            <small>OUTPUT</small>
                            {m.output}
                          </div>
                        </div>
                        <p className="note">{m.limitation}</p>
                        <Button
                          onClick={() => {
                            mutate((d) => {
                              d.modelRuns[m.id] = "Completed";
                              log(
                                d,
                                "Model simulation run",
                                m.id,
                                m.name +
                                  " · deterministic demo output, not live model inference.",
                              );
                            }, "Simulated model run recorded.");
                            inspect(
                              m.name + " · model card",
                              <>
                                <div className="detail-grid">
                                  <div>
                                    <small>Model ID</small>
                                    <strong>{m.id}</strong>
                                  </div>
                                  <div>
                                    <small>Readiness</small>
                                    <strong>{m.stage}</strong>
                                  </div>
                                </div>
                                <h3>Illustrative output</h3>
                                <pre>
                                  {JSON.stringify(
                                    {
                                      model: m.id,
                                      mode: "simulation",
                                      lab: s.lab,
                                      input_record: "TRACE-884",
                                      output:
                                        m.id === "M-01"
                                          ? {
                                              anomaly_score: 0.82,
                                              threshold: 0.7,
                                              window_start: "07:32",
                                            }
                                          : m.id === "M-04"
                                            ? {
                                                hypothesis:
                                                  "Poor flask-to-heater contact",
                                                evidence: [
                                                  "WO-301",
                                                  "CAL-184",
                                                  "CTX-01",
                                                ],
                                              }
                                            : {
                                                candidate_signal:
                                                  "Review recommended",
                                                confidence: "Not calibrated",
                                              },
                                      validation:
                                        "No production validation asserted",
                                    },
                                    null,
                                    2,
                                  )}
                                </pre>
                                <p>{m.limitation}</p>
                                <p>
                                  Before activation: document representative
                                  training data, holdout evaluation, false-alert
                                  tolerance, drift monitoring and lab approval.
                                </p>
                              </>,
                            );
                          }}
                        >
                          <Play size={13} />
                          {s.modelRuns[m.id]
                            ? "Inspect simulated run"
                            : "Run sample simulation"}
                        </Button>
                      </div>
                    </Panel>
                  ))}
              </div>
            </>
          )}

          {page === "wiki" && <KnowledgeHub s={s} mutate={mutate} go={go} />}

          {page === "marketplace" && (
            <PartsCatalog s={s} mutate={mutate} go={go} />
          )}

          {page === "orders" && <Ordering s={s} mutate={mutate} go={go} />}
          {page === "planning" && <SamplePlanner key={lab} s={s} mutate={mutate} go={go} />}
          {page === "aiops" && <AIOperations key={lab} value={governance} onChange={saveGovernance} />}
          {page === "bot" && <LabBot s={s} mutate={mutate} go={go} governance={governance} consume={(id:string)=>saveGovernance(consumeAction(governance,id))} />}
          {page === "enterprise" && (
            <EnterpriseAccess key={lab} s={s} mutate={mutate} />
          )}
          {page === "export" && (
            <>
              <div className="compact-banner">
                <div>
                  <strong>Customer delivery · controlled result export</strong>
                  <span>
                    Download-only demo destination · original results remain in
                    the evidence package.
                  </span>
                </div>
                {badge(
                  s.delivered ? "12 authorized results" : "Release required",
                  s.delivered ? "success" : "amber",
                )}
              </div>
              <div className="two-grid">
                <Panel title="Export configuration">
                  <div className="panel-body">
                    <label className="form-field">
                      Template
                      <select
                        value={format}
                        onChange={(e) => setFormat(e.target.value)}
                      >
                        <option>CSV</option>
                        <option>JSON</option>
                        <option>XML</option>
                      </select>
                    </label>
                    <label className="form-field">
                      Destination
                      <input
                        value="Browser download · demo LIMS handoff"
                        readOnly
                      />
                    </label>
                    <div className="signal-row">
                      <span>Authorizer</span>
                      <strong>Maya Chen</strong>
                    </div>
                    <div className="signal-row">
                      <span>Retest policy</span>
                      <strong>Latest approved result</strong>
                    </div>
                    <p className="note">
                      Held and unreviewed results are blocked. Exporting creates
                      a retained ledger snapshot. No external LIMS or customer
                      message is sent.
                    </p>
                    <Button
                      primary
                      disabled={!s.delivered}
                      onClick={() => {
                        try {
                          const payload = exportPayload(s, format);
                          const id =
                            "EXP-" +
                            String(s.exports.length + 1).padStart(3, "0");
                          mutate((d) => {
                            d.exports.push({
                              id,
                              at: new Date().toISOString(),
                              format,
                              sampleIds: d.samples.map((x) => x.id),
                              payload,
                            });
                            log(
                              d,
                              "Results exported",
                              id,
                              `${format} download; 12 authorized results, 6 retests.`,
                            );
                          }, "Export recorded and downloaded.");
                          Download(
                            "Trucision-" + id + "." + format.toLowerCase(),
                            payload,
                            format === "CSV"
                              ? "text/csv"
                              : format === "XML"
                                ? "application/xml"
                                : "application/json",
                          );
                        } catch (e) {
                          setToast((e as Error).message);
                        }
                      }}
                    >
                      <ArrowDownToLine size={15} /> Export authorized results
                    </Button>
                    {!s.delivered && (
                      <div className="section-gap">
                        <button
                          className="text-link"
                          onClick={() => go("samples")}
                        >
                          Open release queue →
                        </button>
                      </div>
                    )}
                  </div>
                </Panel>
                <Panel title="Field mapping">
                  <table>
                    <thead>
                      <tr>
                        <th>Lab field</th>
                        <th>Export field</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["Sample ID", "sample_id"],
                        ["Customer order", "order"],
                        ["Approved T90 / retest", "result"],
                        ["Method unit", "unit = degC"],
                        ["Original instrument", "original_instrument"],
                        ["Retest instrument", "result_instrument"],
                        ["Superseded result flag", "supersedes_original"],
                      ].map(([a, b]) => (
                        <tr key={a}>
                          <td>{a}</td>
                          <td className="mono">{b}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Panel>
              </div>
              <Panel title="Payload preview">
                <pre className="payload">
                  {s.delivered
                    ? exportPayload(s, format)
                    : "Preview locked until results are authorized by the lab manager."}
                </pre>
              </Panel>
              <Panel title="Export ledger">
                <table>
                  <thead>
                    <tr>
                      <th>Export</th>
                      <th>Format</th>
                      <th>Results</th>
                      <th>Recorded</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {s.exports.map((e) => (
                      <tr key={e.id}>
                        <td>{e.id}</td>
                        <td>{e.format}</td>
                        <td>{e.sampleIds.length}</td>
                        <td>{new Date(e.at).toLocaleString()}</td>
                        <td>
                          <button
                            className="text-link"
                            onClick={() =>
                              Download(
                                e.id + "." + e.format.toLowerCase(),
                                e.payload,
                              )
                            }
                          >
                            Download snapshot
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!s.exports.length && (
                  <div className="empty">
                    No exports yet. Completed deliveries will appear here.
                  </div>
                )}
              </Panel>
            </>
          )}
          <footer className="page-footer">
            <span>
              <span className="live-dot" /> Trucision Lab OS
            </span>
            <span>
              Synthetic lab scenario · browser-persisted demo · human decisions,
              traceable evidence
            </span>
          </footer>
        </main>
      </div>
      {toast && (
        <div className="toast" role="status">
          <CheckCircle2 size={17} />
          {toast}
          <button
            aria-label="Dismiss notification"
            onClick={() => setToast("")}
          >
            <X size={15} />
          </button>
        </div>
      )}
      {modal && (
        <div className="modal-backdrop" onClick={() => setModal(null)}>
          <section
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label={modal.title}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel-head">
              <h2>{modal.title}</h2>
              <button
                autoFocus
                className="icon-button"
                aria-label="Close dialog"
                onClick={() => setModal(null)}
              >
                <X size={18} />
              </button>
            </div>
            <div className="modal-body">{modal.body}</div>
          </section>
        </div>
      )}
    </div>
  );
}
function Metric({
  label,
  value,
  detail,
  icon,
  tone = "",
}: {
  label: string;
  value: string;
  detail: string;
  icon?: ReactNode;
  tone?: string;
}) {
  return (
    <div className={`metric ${tone} ${value.length > 7 ? "metric-text" : ""}`}>
      <div>
        <span>{label}</span>
        {icon}
      </div>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}
function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (s: string) => void;
  placeholder: string;
}) {
  return (
    <label className="search">
      <Search size={15} />
      <input
        aria-label={placeholder}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
